<template>
  <div class="order-details-component">
    <div class="details-container flex">
      <div class="details-content flex a-center">
        <div class="address-section d-flex flex-column">
          <span class="label">Адрес доставки:</span>
          <span
            v-if="order.clientAddress"
            class="address-title"
          >
            <font-awesome-icon
              color="#000000"
              icon="map-marker-alt"
            />
            {{ order.clientAddress.street }} д. {{ order.clientAddress.house }},
            кв {{ order.clientAddress.apartmentNumber }}
          </span>
          <span
            v-else-if="order.deliveryType == 'self'"
            class="address-title pickup"
          >Самовывоз</span>
          <span
            v-else
            class="address-title"
          >–</span>
          <span class="courier">
            <font-awesome-icon
              color="#000000"
              icon="walking"
            />
            {{ getCourierInfo }}
          </span>
        </div>

        <div class="divider" />

        <div class="payment-info-section d-flex">
          <span class="pay-number">
            {{ order.totalPrice | amountWithCurrency0 }}
            <promo-code-badge
              class="promocode"
              :promo-code="order.basket.promoCode"
              :discount-amount="order.discountAmount"
            />
            <span
              v-if="showFoxCoinPay"
              class="foxcoins-pay"
            >-{{ order.foxCoinsPayAmount }}</span>
          </span>

          <div class="info-section d-flex flex-column">
            <span class="label">Тип оплаты:</span>
            <span class="pay-type font-weight-medium">
              <font-awesome-icon
                color="#000000"
                icon="wallet"
              />
              {{ getPaymentType }}
            </span>

            <payment-status-tag style="margin-top: -5px;" :order="order" />

            <span
              v-if="order.basket.clientMoney && order.paymentType === 'cash'"
              class="pay-change"
            >
              <font-awesome-icon
                color="#000000"
                icon="coins"
              />
              сдача с
              <span class="font-weight-medium">{{ order.basket.clientMoney | amountWithCurrency0 }}</span>
            </span>
          </div>
        </div>

        <div class="divider" />

        <div class="branch-section d-flex flex-column">
          <span class="label">Филиал:</span>
          <span class="branch-info">
            <font-awesome-icon
              color="#000000"
              icon="store-alt"
            />
            <span class="font-weight-medium">{{ getFilialName }}</span>
          </span>
          <div class="order-deadline-time">
            <font-awesome-icon
              icon="clock"
              class="mr-3"
            />
            <span class="font-weight-medium">~{{ getOrderDeadlineTime }} / {{ getOrderRemainingMinutes }}</span>
          </div>
        </div>
        <div class="mobile-promocode-section">
          <promo-code-badge
            class="promocode"
            :promo-code="order.basket.promoCode"
            :discount-amount="order.discountAmount"
          />
          <span
            v-if="showFoxCoinPay"
            class="foxcoins-pay"
          >-{{ order.foxCoinsPayAmount }}</span>
        </div>
      </div>

      <div class="details-timer-section flex a-center j-between">
        <div class="remaining-time-section d-flex flex-column">
          <template v-if="getOrderDeadlineTime">
            <span
              v-if="order.orderStatus !== 'completed' && order.orderStatus !== 'canceled'"
              class="timer font-weight-medium"
              :class="{'timer-negative': timeDifference < 0}"
            >
              {{ deadLineTime }}
            </span>
            <span
              v-if="order.orderStatus === 'completed' || order.orderStatus === 'canceled'"
              class="timer font-weight-medium"
              :class="{'timer-negative': timeDifference < 0}"
            >
              {{ deadLineTime }}
            </span>
            <span
              v-if="timeDifference >= 0"
              class="label"
            >Осталось <br />
              времени</span>
            <span
              v-if="timeDifference < 0"
              class="label"
            >Опоздание <br />
              заказа</span>
          </template>
        </div>

        <div class="order-details-section flex flex-column a-center">
          <button
            class="button-info"
            :class="{'active': showFoodInfo}"
            @click="openFoodInfo"
          >
            <font-awesome-icon
              color="#DBDFE6"
              icon="chevron-circle-down"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import { DateTime, Duration } from 'luxon'
  import PaymentStatusTag from '@/components/order-card/PaymentStatusTag.vue'
  import PromoCodeAction from '@/models/enums/PromoCodeAction'
  import PaymentType from '@/models/enums/PaymentType'
  import PaymentMode from '@/models/enums/PaymentMode'
  import DeliveryType from '@/models/enums/DeliveryType'
  import { formatIsoDateTimeAsTime } from '@/util/date-time-helper'

  export default {
    name: 'OrderDetails',
    components: { PaymentStatusTag },
    mixins: [mixinSnackbarMessage],
    props: {
      order: {
        type: Object,
        default: () => ({}),
      },
    },
    data () {
      return {
        showFoodInfo: false,
        deadLineTime: '00:00',
        deadLineTimeout: null,
        timeDifference: 0,
      }
    },
    computed: {
      showFoxCoinPay () {
        return this.order.basket.promoCode &&
          this.order.basket.promoCode.promoCode &&
          this.order.basket.promoCode.promoCode.action === PromoCodeAction.FoxCoinPay
      },
      getOrderDeadlineTime () {
        return formatIsoDateTimeAsTime(this.order.deadLine, '')
      },
      getOrderRemainingMinutes () {
        return (this.order.basket.cookingAndDeliveryTime / 60) + ' мин'
      },
      getCourierInfo () {
        if (this.order.courier) {
          return this.order.courier.name
        }

        if (this.order.basket.basketSource === 'deliveryСlub') {
          return 'Delivery Club'
        }

        if (this.order.basket.basketSource === 'yandex') {
          return 'Яндекс.Еда'
        }

        if (this.order.basket.deliveryType === DeliveryType.Aggregator) {
          return 'Курьер агрегатора'
        }

        return '-'
      },
      getFilialName () {
        return this.order.filial ? this.order.filial.name : '–'
      },
      getPaymentType () {
        if (this.order.paymentType) {
          return PaymentType.getName(this.order.paymentType, this.order.deliveryType, PaymentMode.ofOrder(this.order))
        }
        return '–'
      },
    },
    watch: {
      timeDifference () {
        const isTimerPositive = this.timeDifference > 0
        this.$emit('updateStepperStatus', isTimerPositive)
      },
      order: {
        handler () {
          if (this.order.orderStatus === 'completed' || this.order.orderStatus === 'canceled') {
            clearInterval(this.deadLineTimeout)
            this.getRemainingTime()
          } else {
            this.setTimer()
          }
        },
        deep: true,
      },
    },
    mounted () {
      if (this.order.orderStatus !== 'completed' && this.order.orderStatus !== 'canceled') {
        this.setTimer()
      } else {
        this.getRemainingTime()
      }
    },
    beforeDestroy () {
      clearInterval(this.deadLineTimeout)
    },
    methods: {
      emitCallback () {
        if (this.order.client.phone) {
          const payload = {
            id: this.order.id,
          }
          this.$store.dispatch('orders/emitCallback', payload)
            .then(res => {
              if (res.status === 200) {
                this.showSuccessMessage('Обратный звонок клиенту инициирован')
              } else {
                this.showErrorMessage('Возникла ошибка')
              }
            })
        }
      },
      openFoodInfo () {
        this.showFoodInfo = !this.showFoodInfo
        console.log(this.showFoodInfo)
        this.$emit('openFoodInfo')
      },
      getRemainingTime () {
        if (!this.order.deadLineString) {
          return false
        }
        let completeDatatime = ''
        if (this.order.orderStatus === 'completed') {
          completeDatatime = DateTime.fromFormat(this.order.orderCompletedAt, 'yyyy-MM-dd HH:mm:ss').toMillis()
        } else {
          completeDatatime = DateTime.fromFormat(this.order.orderCanceledAt, 'yyyy-MM-dd HH:mm:ss').toMillis()
        }
        const deadLine = DateTime.fromFormat(this.order.deadLineString, 'yyyy-MM-dd HH:mm:ss').toMillis()
        this.timeDifference = Duration.fromMillis(deadLine - completeDatatime).as('milliseconds')
        this.deadLineTime = Duration.fromMillis(Math.abs(this.timeDifference)).toFormat('mm:ss')
        if (this.timeDifference < 0) {
          this.deadLineTime = `-${this.deadLineTime}`
        }
      },
      setTimer () {
        if (!this.order.deadLineString) {
          return false
        }
        const currentDatetime = DateTime.local().toMillis()
        const deadLine = DateTime.fromFormat(this.order.deadLineString, 'yyyy-MM-dd HH:mm:ss').toMillis()
        this.timeDifference = Duration.fromMillis(deadLine - currentDatetime).as('milliseconds')
        this.timeDifference = Math.round(this.timeDifference)
        clearInterval(this.deadLineTimeout)
        this.deadLineTimeout = setInterval(() => {
          const minutes = Duration.fromMillis(Math.abs(this.timeDifference)).toFormat('mm:ss')
          if (this.timeDifference < 0) {
            this.deadLineTime = `-${minutes}`
          } else {
            this.deadLineTime = `${minutes}`
          }
          this.timeDifference -= 1000
        }, 1000)
      },
    },
  }
</script>

<style lang="less">
  //noinspection ALL
  .order-details-component {
    display: flex;
    align-items: center;
    padding-left: 5px;
    padding-right: 20px;
    padding-top: 25px;

    font-size: 12px;

    .media-xl({
      font-size: 14px;
    });

    .client-phone-section {
      button.client-phone {
        cursor: pointer;
        text-align: left;
      }

      span.client-phone {
        cursor: default;
      }
      .client-phone {
        padding: 9px 21px 7px 16px;
        margin-right: 24px;
        border-radius: 100px;
        text-decoration: none;
        background-color: #ffffff;
        border: 1px solid #000000;
        color: #000000;
        max-width: 200px;
        .fill-svg(#000000);

        @media (max-width: 1700px) {
          margin-right: 15px;
        }

        &.client-phone-active {
          background-color: @color-primary;
          border: 1px solid @color-primary;
          color: #ffffff;
          .fill-svg(#ffffff);
        }

        .name {
          font-size: 14px;
          line-height: 17px;
        }

        /*.number {
          font-size: 16px;
          line-height: 19px;
        }*/

        svg {
          width: 17px;
          margin-right: 12px;
        }
      }
    }

    .divider {
      height: 68px;
      width: 1px;
      background-color: #dbdbdb;
    }

    .address-section {
      margin-left: 5px;
      margin-right: 5px;

      span {
        display: inline-block;
        color: #000000;

        svg {
          margin-right: 7px;
        }
      }

      .label {
        margin-bottom: 5px;
        font-size: 14px;
        line-height: 17px;
      }

      .address-title {
        margin-bottom: 12px;
        font-size: 16px;
        line-height: 19px;

        &.pickup {
          //color: #38b2b2;
          color: #e91e63;
          font-weight: bold;
        }
      }
    }

    .mobile-promocode-section {
      display: none;
    }

    // Рамка и скругление - в PromoCodeBadge, здесь только размер и место
    .promocode {
      font-size: 12px;
      min-height: 24px;
      margin-top: 5px;
      max-width: 150px;
      padding: 0 4px;
    }

    .foxcoins-pay {
      font-size: 16px;
      color: #fe9923;
      display: flex;
      align-items: center;
      line-height: 1.2;
      font-weight: bold;

      &:after {
        display: inline-block;
        height: 20px;
        width: 20px;
        content: '';
        background: url("/img/icons/setcoins-icon.svg") no-repeat center center;
        background-size: contain;
        margin-left: 2px;
        margin-top: -3px;
      }

    }

    .payment-info-section {
      margin-left: 15px;
      margin-right: 15px;

      @media (max-width: 1700px) {
        flex-direction: column;
      }

      .label {
        line-height: 1;
      }

      .pay-number {
        margin-right: 15px;
        font-size: 20px;
        line-height: 1.3;

        .media-xl({
          font-size: 30px;
        })
      }

      .info-section {
        span {
          display: inline-block;
        }

        .pay-type {
          margin-bottom: 12px;

          svg {
            margin-right: 8px;
          }
        }

        .pay-change {
          line-height: 19px;

          svg {
            margin-right: 8px;
          }
        }
      }
    }

    .branch-section {
      align-self: stretch;
      margin-left: 16px;

      .branch-info {
        svg {
          margin-right: 8px;
        }
      }
    }

    .remaining-time-section {
      margin-left: 20px;
      margin-right: 11px;

      .timer {
        color: #1ee984;
        font-size: 25px;
        line-height: 30px;
        &.timer-negative {
          color: #E91E63;
        }
      }

      .label {
        //font-size: 14px;
        //line-height: 1;
      }
    }

    .order-details-section {
      .label {
        font-size: 14px;
      }

      .button-info {
        width: 30px;
        height: 30px;

        &.active {
          svg {
            transform: rotate(180deg);
          }
        }

        svg {
          width: 30px;
          height: 30px;
        }
      }
    }

    @media (min-width: @md) {
      .details-container {

        .details-content {
          & > .d-flex {
            flex-grow: 1;
          }
          .client-phone-section {
            //flex-basis: 21%;

            .client-phone {
              min-height: 54px;
              .name.anonim {
                text-align: center;
                margin-top: -3px;
              }
            }
          }

          .address-section {
            flex-basis: 20%;
            max-height: 80px;
            justify-content: space-around;
          }

        }

        .details-timer-section {
          max-width: 215px;
          .order-details-section {
            max-width: 30px;

            svg {
              margin-bottom: 6px;
            }

            .label {
              line-height: 1.1;
            }
          }

          .remaining-time-section {
            min-width: 80px;
          }
        }

      }
    }

    .media-lg({
      padding: 15px 10px;
      .divider {
        display: none;
      }
      .details-container {
        flex-direction: column;
      }
      .details-content {
        flex-direction: column;
        align-items: flex-start;
      }
      .mobile-promocode-section {
        display: block;
        align-self: stretch;
        margin-bottom: 16px;
      }
      .client-phone-section {
        display: none;
        /*margin-right: 0;*/
        /*width: 100%;*/
        /*margin-bottom: 10px;*/
        /*display: flex;*/
        /*justify-content: center;*/
        .client-phone {
          width: 100%;
          margin-right: 0;
        }
      }
      .address-section {
        width: 100%;
        margin-bottom: 10px;
      }

      .payment-info-section {
        margin: 0 0 10px 0;
        width: 100%;
        justify-content: flex-start;

        .pay-number {
          display: none;
        }
      }

      .branch-section {
        margin-left: 0;
        margin-bottom: 15px;
        .order-deadline-time {
          display: none;
        }
      }

      .remaining-time-section {
        margin-left: 0;
        margin-right: 0;
        flex: 1;
        justify-content: center;
        display: none !important;
        span {
          text-align: center;
        }
      }

      .details-timer-section {
        justify-content: center !important;
        padding: 10px 0 5px 0;
        border-top: 1px solid rgba(#333333, 0.2);
        border-bottom: 1px solid rgba(#333333, 0.2);
      }
      .order-details-section {
        flex: 1;
      }
    })
  }
</style>
